/**
 * Form Storage Service
 * Uploads physical admission form images to Firebase Storage
 * and attaches the URL to the CRM lead record
 */

import { storage } from '../@obis/firebase-config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];

export function validateFormImage(file: File): string | null {
  if (!ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
    return 'Please upload a JPG, PNG, or WebP image of the form';
  }
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    return `Image must be under ${MAX_FILE_SIZE_MB}MB`;
  }
  return null;
}

export async function uploadAdmissionForm(
  file: File,
  branchId: string,
  leadId: string
): Promise<string> {
  return uploadLeadDocument(file, branchId, leadId, 'admission_form');
}

export async function uploadLeadDocument(
  file: File,
  branchId: string,
  leadId: string,
  documentType: 'admission_form' | 'birth_certificate' | 'other'
): Promise<string> {
  const timestamp = Date.now();
  const ext = file.name.split('.').pop() || 'jpg';
  
  // Structured folder name
  const folder = documentType === 'admission_form' ? 'forms' : 'certificates';
  const path = `leads/${branchId}/${leadId}/${folder}/${timestamp}.${ext}`;

  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file, {
    contentType: file.type,
    customMetadata: {
      branchId,
      leadId,
      documentType,
      uploadedAt: new Date().toISOString(),
    }
  });

  const downloadUrl = await getDownloadURL(snapshot.ref);
  return downloadUrl;
}

export function createObjectURL(file: File): string {
  return URL.createObjectURL(file);
}

export function revokeObjectURL(url: string): void {
  URL.revokeObjectURL(url);
}
